/**
 * Liberação da estrutura automática de preços: forma na tela ↔ forma no ad.js.
 *
 * O contrato que o script-rocket lê tem uma armadilha: `[]` libera **nada** e
 * `[{}]` libera **tudo**. São opostos separados por um par de chaves, e `[{}]`
 * é indistinguível de "uma linha de filtro em branco que o usuário ainda está
 * preenchendo". Por isso o modo é um campo próprio da tela em vez de ser
 * deduzido das linhas, e a conversão para o formato salvo mora aqui — num só
 * lugar, longe do JSX.
 */

/** Campos que o script compara. Não existe UTM: ver `paraSalvar`. */
export interface StructureScope {
    country?: string;
    id_post_wp?: string;
    ad_unit?: string;
}

export type StructureMode = 'tudo' | 'filtros';

/**
 * O que está marcado na tela, por dimensão.
 *
 * Dimensão sem nada marcado é curinga — não marcar país nenhum significa
 * "todos os países", não "nenhum". É como o escopo funciona do outro lado:
 * campo omitido não restringe.
 */
export interface Selection {
    countries: string[];
    posts: string[];
    blocks: string[];
}

export const SELECAO_VAZIA: Selection = { countries: [], posts: [], blocks: [] };

/** Estado da tela. `mode` não é persistido — some no `paraSalvar`. */
export interface StructureCascateDraft {
    active: boolean;
    mode: StructureMode;
    selection: Selection;
}

const CAMPOS: (keyof StructureScope)[] = ['country', 'id_post_wp', 'ad_unit'];

export const escopoEmBranco = (scope: StructureScope): boolean =>
    CAMPOS.every((campo) => !(scope[campo] ?? '').trim());

const aparado = (scope: StructureScope): StructureScope => {
    const limpo: StructureScope = {};
    for (const campo of CAMPOS) {
        const valor = (scope[campo] ?? '').trim();
        if (valor) limpo[campo] = valor;
    }
    return limpo;
};

/**
 * Marcações viram a lista de escopos que o script lê.
 *
 * É o produto cartesiano das dimensões marcadas: marcar Brasil e Portugal com
 * os posts 10 e 20 quer dizer "esses posts, nesses países" — quatro entradas.
 * Fosse uma entrada por marcação, o OU entre elas liberaria os dois posts em
 * *qualquer* país, que é bem mais do que foi pedido.
 *
 * Dimensão sem marcação entra como curinga e não multiplica nada.
 */
export function paraEscopos(sel: Selection): StructureScope[] {
    const countries = sel.countries.length ? sel.countries : [undefined];
    const posts = sel.posts.length ? sel.posts : [undefined];
    const blocks = sel.blocks.length ? sel.blocks : [undefined];

    const escopos: StructureScope[] = [];
    for (const country of countries)
        for (const id_post_wp of posts)
            for (const ad_unit of blocks)
                escopos.push(aparado({ country, id_post_wp, ad_unit }));

    return escopos;
}

/** Quantas entradas a seleção vai gerar, sem construir a lista. */
export const contaEscopos = (sel: Selection): number =>
    Math.max(1, sel.countries.length) *
    Math.max(1, sel.posts.length) *
    Math.max(1, sel.blocks.length);

/** As marcações que geram esta lista, se ela for um produto cartesiano. */
function daEscopos(scopes: StructureScope[]): Selection {
    const unicos = (campo: keyof StructureScope) => [
        ...new Set(scopes.map((s) => s[campo]).filter((v): v is string => !!v)),
    ];
    return {
        countries: unicos('country'),
        posts: unicos('id_post_wp'),
        blocks: unicos('ad_unit'),
    };
}

const mesmaLista = (a: StructureScope[], b: StructureScope[]) => {
    const chave = (s: StructureScope[]) =>
        JSON.stringify([...s.map((x) => JSON.stringify(aparado(x)))].sort());
    return chave(a) === chave(b);
};

/**
 * O que veio da API vira estado editável.
 *
 * `representavel: false` avisa que o escopo salvo não se desmonta em marcações
 * — alguém escreveu à mão uma combinação que a tela não sabe desenhar. Nesse
 * caso ela mostra o JSON cru e só sobrescreve se você mexer em algo, para não
 * destruir em silêncio uma configuração que funciona.
 */
export function daApi(
    active: unknown,
    scopes: unknown,
): StructureCascateDraft & { representavel: boolean; original: StructureScope[] } {
    const lista = (Array.isArray(scopes) ? (scopes as StructureScope[]) : []).map(aparado);
    const tudo = lista.length === 1 && escopoEmBranco(lista[0] ?? {});
    const selection = tudo ? SELECAO_VAZIA : daEscopos(lista);

    return {
        active: Number(active) === 1,
        mode: tudo ? 'tudo' : 'filtros',
        selection,
        original: lista,
        representavel: tudo || lista.length === 0 || mesmaLista(paraEscopos(selection), lista),
    };
}

/**
 * Estado da tela vira o que a API grava.
 *
 * Sem nenhuma marcação no modo de filtros o resultado é `[]`, que não libera
 * nada — e não `[{}]`, que liberaria o domínio inteiro. Errar para o lado de
 * não liberar é o lado recuperável.
 *
 * Não existe campo de UTM aqui de propósito. O script ignora campo que não
 * conhece e considera a entrada casada, então `{utm_source: "x"}` liberaria
 * tudo passando por filtro.
 */
export function paraSalvar(draft: StructureCascateDraft): {
    active_structure_cascate: number;
    structure_cascate: StructureScope[];
} {
    const { selection } = draft;
    const nadaMarcado =
        !selection.countries.length && !selection.posts.length && !selection.blocks.length;

    return {
        active_structure_cascate: draft.active ? 1 : 0,
        structure_cascate:
            draft.mode === 'tudo' ? [{}] : nadaMarcado ? [] : paraEscopos(selection),
    };
}
